import { universalTypeFilterId } from '../../../common/constants/filters';
import type { AllFilterStates } from '../../../common/constants/filters/types';
import { type TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { type ProductKeys } from '../../../common/constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { SearchStateFilters } from '../../../common/constants/search';

import { getEntitiesForQuery as getUniversalEntitiesForQuery } from './filters/universal-type/utils';

/** Return type for type entity lookups — maps entity keys to display labels. */
export type UseTypeEntitiesResult = {
	selectedEntities: string[];
	entitiesForQuery: import('@atlassian/search-client').EntityATI[];
	entitiesForResultsCountQuery: import('@atlassian/search-client').EntityATI[];
	selectedTypes: TypeFilterValueKey[];
};

/** The search state fields needed to compute type entities. */
export type TypeEntitiesInput = {
	availableProducts: ProductKeys[];
	filters: SearchStateFilters;
	selectedProducts: ProductKeys[];
	allFilterExcludedProducts: ProductKeys[];
};

/** Configuration for type entity resolution (third-party integrations). */
export type TypeEntitiesConfig = {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
};

/**
 * Returns type entities for the "universal" (cross-product) search scope,
 * merging all available product type entities and applying third-party config overrides.
 *
 * Pure function — safe for SSR and non-React contexts.
 */
export const getUniversalTypeEntities = (
	productsToSearch: ProductKeys[],
	typeFilter: AllFilterStates | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	availableProducts: ProductKeys[],
	selectedProducts: ProductKeys[] | undefined,
) => {
	// This early return is to appease Typescript. The typeFilter is always defined and is always single select.
	if (!typeFilter || !(typeFilter.type === 'single-select')) {
		return {
			selectedEntities: [],
			entitiesForQuery: [],
			entitiesForResultsCountQuery: [],
			selectedTypes: [],
		};
	}
	const defaultOptionValue = typeFilter.options?.find((option) => option.isDefault)?.value;

	const selectedTypes = (typeFilter.value as TypeFilterValueKey[])
		.filter((value) => value !== defaultOptionValue)
		.filter(Boolean);

	const { entitiesForQuery, entitiesForResultsCountQuery } = getUniversalEntitiesForQuery(
		productsToSearch,
		selectedTypes,
		thirdPartyConfigs,
		availableProducts,
		selectedProducts,
	);

	return {
		selectedEntities: selectedTypes,
		entitiesForQuery: entitiesForQuery,
		entitiesForResultsCountQuery,
		selectedTypes,
	};
};

/**
 * Returns the set of content-type entity identifiers (e.g. pages, issues, whiteboards)
 * that should be included in a search query, based on the currently selected products
 * and available filter configuration.
 *
 * Pure function — safe for SSR and non-React contexts.
 */
export const getTypeEntities = (
	{ availableProducts, filters, selectedProducts, allFilterExcludedProducts }: TypeEntitiesInput,
	{ thirdPartyConfigs }: TypeEntitiesConfig,
) => {
	// Filter out excluded products from "All" filter
	const allFilterIncludedProducts = availableProducts.filter(
		(product) => !allFilterExcludedProducts.includes(product),
	);
	const productsToSearch = selectedProducts[0] ? [selectedProducts[0]] : allFilterIncludedProducts;
	const typeFilter = filters[universalTypeFilterId];

	return getUniversalTypeEntities(
		productsToSearch,
		typeFilter,
		thirdPartyConfigs,
		availableProducts,
		selectedProducts,
	);
};
